/**
 * AdminDanismanYonetimi.jsx
 *
 * Danışman Yönetimi — danışmanları satır satır listeleyen, aktif/pasif
 * durumunu yöneten dedike sayfa (bkz. proje-yoneticisi görev talimatı,
 * 2026-08-13 — İş B). Önceki turda danışmanları tek tek listeleyen bir
 * yönetim ekranı YOKTU; select'lerde ad-hoc görünürlerdi.
 *
 * Route: /admin/danismanlar
 * Erişim: admin, superadmin (danışman rolü erişemez).
 *
 * Backend: routes/leads.py
 *   GET /admin/danismanlar?aktif=tumu&include_admins=false
 *   PUT /admin/danismanlar/{user_id}/aktif  body {"aktif": true|false}
 *
 * ÖNEMLİ (bkz. backend docstring): pasif işaretlemek yalnızca YENİ atama
 * listelerinde danışmanı gizler — mevcut lead/satış/görüşme/rapor verisi
 * ETKİLENMEZ, login akışına DOKUNULMAZ. Bu sayfa kasıtlı olarak KÜÇÜK
 * kalır — yeni rapor/istatistik eklenmez.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import ConfirmDialog from '../components/admin/ConfirmDialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage, getApiErrorCode } from '../utils/apiError';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Users2, Search, AlertTriangle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { value: 'tumu', tr: 'Tümü', en: 'All' },
  { value: 'true', tr: 'Aktif', en: 'Active' },
  { value: 'false', tr: 'Pasif', en: 'Inactive' },
];

function ErrorRetry({ onRetry, tr }) {
  return (
    <div
      className="p-3 rounded-xl flex items-center justify-between gap-3"
      style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)' }}
    >
      <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--crm-danger)' }}>
        <AlertTriangle size={16} /> {tr('Bağlantı koptu — tekrar dene', 'Connection lost — try again')}
      </span>
      <button
        onClick={onRetry}
        className="px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1 flex-shrink-0"
        style={{ backgroundColor: 'var(--crm-danger)' }}
      >
        <RefreshCw size={13} /> {tr('Tekrar Dene', 'Retry')}
      </button>
    </div>
  );
}

export default function AdminDanismanYonetimi() {
  const { lang } = useLanguage();
  const { authHeaders, user } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [danismanlar, setDanismanlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('tumu');

  // Toggle akışı — pasife alırken onay Dialog'u, aktife alırken doğrudan.
  const [togglingId, setTogglingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // pasife alınacak danışman
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [rowErrors, setRowErrors] = useState({});

  const fetchDanismanlar = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      // Bu sayfa danışmanları YÖNETMEK içindir — admin/superadmin karışmasın
      // diye include_admins=false. Filtre her zaman aktif=tumu ile çekilir,
      // görünürlük client-side filtrelenir (arama + durum chip'i).
      const r = await axios.get(
        `${API}/admin/danismanlar?aktif=tumu&include_admins=false`,
        { headers: authHeaders() }
      );
      setDanismanlar(r.data.danismanlar || []);
    } catch (err) {
      setError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => { fetchDanismanlar(); }, [fetchDanismanlar]);

  const applyToggle = async (userId, aktif) => {
    setTogglingId(userId);
    setRowErrors((prev) => { const n = { ...prev }; delete n[userId]; return n; });
    // Optimistic — hata olursa eski duruma dönülür.
    setDanismanlar((prev) => prev.map((d) => (d.user_id === userId ? { ...d, danisman_aktif: aktif } : d)));
    try {
      await axios.put(
        `${API}/admin/danismanlar/${userId}/aktif`,
        { aktif },
        { headers: authHeaders() }
      );
      toast.success(aktif ? tr('Danışman aktife alındı', 'Consultant activated') : tr('Danışman pasife alındı', 'Consultant deactivated'));
    } catch (err) {
      // Eski duruma dön (rollback) + satırda hata göster.
      setDanismanlar((prev) => prev.map((d) => (d.user_id === userId ? { ...d, danisman_aktif: !aktif } : d)));
      const code = getApiErrorCode(err);
      let msg;
      if (code === 'kullanici_bulunamadi') {
        msg = tr('Kullanıcı bulunamadı', 'User not found');
      } else if (code === 'danisman_degil') {
        msg = tr('Bu kullanıcı danışman rolünde değil', 'This user does not have the consultant role');
      } else if (code === 'gecersiz_aktif') {
        msg = tr("'aktif' alanı boolean olmalı", "'aktif' field must be a boolean");
      } else {
        msg = getApiErrorMessage(err, lang);
      }
      setRowErrors((prev) => ({ ...prev, [userId]: msg }));
      toast.error(msg);
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggle = (d) => {
    if (togglingId) return;
    if (d.danisman_aktif) {
      // Pasife almak — onay iste.
      setConfirmTarget(d);
    } else {
      applyToggle(d.user_id, true);
    }
  };

  const confirmPasifeAl = async () => {
    if (!confirmTarget) return;
    setConfirmLoading(true);
    await applyToggle(confirmTarget.user_id, false);
    setConfirmLoading(false);
    setConfirmTarget(null);
  };

  const filtered = danismanlar.filter((d) => {
    if (filter === 'true' && d.danisman_aktif !== true) return false;
    if (filter === 'false' && d.danisman_aktif !== false) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const hay = `${d.name || ''} ${d.email || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        <PageHeader
          breadcrumbs={[rootBreadcrumb(user?.role, tr), { label: tr('Danışman Yönetimi', 'Consultant Management') }]}
          title={tr('Danışman Yönetimi', 'Consultant Management')}
          subtitle={tr('Danışmanları aktif/pasif olarak işaretleyin — pasif danışman yeni atama listelerinde görünmez.', 'Mark consultants active/inactive — inactive consultants are hidden from new assignment lists.')}
        />

        {/* Arama + filtre */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--crm-muted)' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tr('Ad soyad veya e-posta ara...', 'Search by name or email...')}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 text-sm font-semibold bg-white focus:outline-none focus:ring-2"
              style={{ borderColor: 'var(--crm-input-border)', color: 'var(--crm-navy-text)' }}
            />
          </div>
          <div className="flex gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                aria-pressed={filter === f.value}
                className="px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all"
                style={filter === f.value
                  ? { backgroundColor: 'var(--crm-navy)', color: 'white', borderColor: 'var(--crm-navy)' }
                  : { backgroundColor: 'white', color: 'var(--crm-navy-text)', borderColor: 'var(--crm-card-border)' }}
              >
                {lang === 'tr' ? f.tr : f.en}
              </button>
            ))}
          </div>
        </div>

        {error && !loading && (
          <div className="mb-4"><ErrorRetry onRetry={fetchDanismanlar} tr={tr} /></div>
        )}

        {loading && (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="bg-white p-8 text-center" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
            <Users2 size={32} className="mx-auto mb-2" style={{ color: 'var(--crm-muted-2)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--crm-muted)' }}>
              {danismanlar.length === 0
                ? tr('Henüz danışman kaydı yok.', 'No consultants yet.')
                : tr('Aramanızla eşleşen danışman yok.', 'No consultants match your search.')}
            </p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="bg-white overflow-hidden" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ backgroundColor: 'var(--crm-cream)' }}>
                  <tr>
                    <th scope="col" className="text-left p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('Ad Soyad', 'Name')}</th>
                    <th scope="col" className="text-left p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('E-posta', 'Email')}</th>
                    <th scope="col" className="text-center p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('Durum', 'Status')}</th>
                    <th scope="col" className="text-center p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('İşlem', 'Action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => (
                    <tr key={d.user_id} className="border-t" style={{ borderColor: 'var(--crm-card-border)' }}>
                      <td className="p-3 font-semibold" style={{ color: 'var(--crm-navy-text)' }}>{d.name}</td>
                      <td className="p-3 text-xs" style={{ color: 'var(--crm-muted)' }}>{d.email}</td>
                      <td className="p-3 text-center">
                        <span
                          className="px-2 py-0.5 rounded-full text-[11px] font-bold"
                          style={d.danisman_aktif
                            ? { backgroundColor: 'rgba(29,138,76,0.10)', color: 'var(--crm-success)' }
                            : { backgroundColor: 'var(--crm-cream)', color: 'var(--crm-muted)' }}
                        >
                          {d.danisman_aktif ? tr('Aktif', 'Active') : tr('Pasif', 'Inactive')}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col items-center gap-1">
                          <Switch
                            checked={!!d.danisman_aktif}
                            disabled={togglingId === d.user_id}
                            onCheckedChange={() => handleToggle(d)}
                            aria-label={d.danisman_aktif ? tr('Pasife al', 'Deactivate') : tr('Aktife al', 'Activate')}
                          />
                          {rowErrors[d.user_id] && (
                            <span className="text-[10px] font-bold text-center" style={{ color: 'var(--crm-danger)' }}>
                              {rowErrors[d.user_id]}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Pasife alma onayı */}
      <ConfirmDialog
        open={!!confirmTarget}
        onOpenChange={(o) => { if (!o && !confirmLoading) setConfirmTarget(null); }}
        title={tr('Danışmanı pasife al?', 'Deactivate consultant?')}
        description={tr(
          'Pasif danışman yeni atama listelerinde görünmez. Mevcut lead, satış ve raporları etkilenmez.',
          "A passive consultant is hidden from new assignment lists. Their existing leads, sales and reports are unaffected."
        )}
        variant="warning"
        confirmLabel={tr('Pasife Al', 'Deactivate')}
        cancelLabel={tr('Vazgeç', 'Cancel')}
        onConfirm={confirmPasifeAl}
        loading={confirmLoading}
      />
    </AdminLayout>
  );
}
